/**
 * Toast -- the single global, app-wide feedback layer for the CRM (#2059,
 * Tier 1.5 of the design-system epic #2048).
 *
 * The design audit (`docs/CRM_Design_Audit.md`) flagged this as its #1
 * interaction fix: there was NO global toast/feedback layer. 111 files
 * hand-rolled their own `setError`/`setSuccess` inline banners (all drifting),
 * a handful still used a raw `alert()`, and -- worst -- a successful save just
 * closed the modal with ZERO acknowledgement ("Did it save?" on every
 * create/edit). This primitive establishes one accessible, token-driven,
 * reduced-motion-safe toast that any surface can fire via `useToast()` (the
 * hook + types live in the sibling `toast.ts`, so this module exports only
 * components -- keeping React Fast Refresh clean, the contexts/auth.ts split).
 *
 * Contract:
 *   const toast = useToast();
 *   toast.success("Contact saved.");
 *   toast.error("Failed to save.", { title: "Save failed" });
 *   toast.info("Export started.", { duration: 8000 });
 *   const id = toast.show({ variant: "success", message: "..." });
 *   toast.dismiss(id);   // manual dismiss of a specific toast
 *
 * Behaviour:
 *   - variants success / error / info, each with its semantic token + icon;
 *   - AUTO-dismiss (sensible default; longer for errors so the user can read
 *     the failure) PLUS a manual >=44px dismiss button;
 *   - STACKING: multiple toasts stack (newest on top of the visual pile),
 *     bottom-center on mobile / bottom-right on desktop, capped so a runaway
 *     caller cannot bury the screen (oldest is dropped);
 *   - MOTION: transform + opacity slide/fade-in with an exponential ease-out
 *     (NO bounce). `prefers-reduced-motion` removes the transform entirely
 *     (opacity only) via the CSS rule in index.css -- the JS never animates.
 *   - ACCESSIBLE: the live region is aria-live="polite" for success/info and
 *     "assertive" for errors (role="alert"); the dismiss button is a real,
 *     focusable <button> with an aria-label.
 *
 * Additive only -- a toast NEVER replaces an existing inline field/validation
 * error; it complements it. This component introduces NO business logic: it
 * renders whatever message a handler passes and calls no API.
 *
 * Styling: Tailwind utility classes + brand CSS-variable tokens only; dark mode
 * via the tokens. Agency-neutral, emoji-free, Lucide icons.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import type { LucideProps } from "lucide-react";
import { ToastContext } from "./useToast";
import type { ToastApi, ToastSpec, ToastVariant } from "./useToast";

interface ToastItem extends ToastSpec {
  id: number;
  /** Resolved auto-dismiss duration (0 = persist). */
  duration: number;
}

// Cap the visible stack so a runaway caller can never bury the UI; the oldest
// toast is dropped when a new one would exceed the cap.
const MAX_TOASTS = 4;
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  error: 7000,
  info: 4000,
};

const VARIANT_ICON: Record<ToastVariant, React.ComponentType<LucideProps>> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

// Each variant's accent (left border + icon) is a semantic token, so it is
// correct in light + dark and agency-neutral. The card surface itself stays
// neutral for readability; the colour is the affordance, not the whole fill.
const VARIANT_ACCENT: Record<ToastVariant, string> = {
  success: "var(--color-success)",
  error: "var(--color-danger)",
  info: "var(--color-info)",
};

/**
 * A single rendered toast. Owns its own mount transition (transform + opacity,
 * exponential ease-out, NO bounce) and its leave transition. Reduced motion is
 * honoured purely in CSS (`.crm-toast` rule in index.css): the transform is
 * dropped and only opacity changes.
 */
function ToastCard({
  item,
  onDismiss,
}: {
  item: ToastItem;
  onDismiss: (id: number) => void;
}) {
  // `entered` drives the enter transition (off-screen/transparent -> in place).
  const [entered, setEntered] = useState(false);
  // `leaving` drives the exit transition before the item is removed.
  const [leaving, setLeaving] = useState(false);
  const Icon = VARIANT_ICON[item.variant];
  const accent = VARIANT_ACCENT[item.variant];
  const timerRef = useRef<number | undefined>(undefined);

  const beginLeave = useCallback(() => {
    setLeaving(true);
    // Remove after the leave transition completes (matches the 200ms duration).
    window.setTimeout(() => onDismiss(item.id), 200);
  }, [item.id, onDismiss]);

  // Enter on next frame so the transition runs from the initial (hidden) class.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Auto-dismiss timer (0 => persist until manual dismiss).
  useEffect(() => {
    if (item.duration <= 0) return;
    timerRef.current = window.setTimeout(beginLeave, item.duration);
    return () => {
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
    };
  }, [item.duration, beginLeave]);

  const isError = item.variant === "error";

  return (
    <div
      // success/info are polite; an error interrupts (assertive + role=alert).
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={
        "crm-toast pointer-events-auto relative w-full max-w-sm overflow-hidden " +
        "rounded-lg border " +
        "border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-md " +
        "border-l-4 " +
        // Enter/leave state classes drive transform + opacity only.
        (leaving
          ? "crm-toast--leave"
          : entered
            ? "crm-toast--enter"
            : "crm-toast--initial")
      }
      style={{ borderLeftColor: accent }}
      data-testid="crm-toast"
      data-variant={item.variant}
    >
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <Icon
          className="mt-0.5 h-5 w-5 shrink-0"
          style={{ color: accent }}
          aria-hidden="true"
          strokeWidth={2}
        />
        <div className="min-w-0 flex-1">
          {item.title && (
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
              {item.title}
            </p>
          )}
          <p
            className={
              "text-sm text-[var(--color-text-secondary)] break-words" +
              (item.title ? " mt-0.5" : "")
            }
          >
            {item.message}
          </p>
          {item.action && (
            <button
              type="button"
              onClick={() => {
                item.action?.onClick();
                beginLeave();
              }}
              className="mt-2 inline-flex min-h-[44px] items-center text-sm font-medium text-brand-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50 rounded"
            >
              {item.action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={beginLeave}
          // >=44px target (WCAG 2.5.5) without enlarging the toast row: the hit
          // area is padded; the visible icon stays compact.
          className="-m-1.5 shrink-0 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {/* #2580: undo-toast countdown affordance -- a thin line along the bottom
          that sweeps to zero over the auto-dismiss window (the visible "time
          left to Undo"). Motion-safe: reduced-motion users get a static full
          line (`.crm-toast-countdown` has no animation under prefers-reduced-
          motion). Only rendered for a countdown toast with a real duration. */}
      {item.countdown && item.duration > 0 && !leaving && (
        <div
          className="crm-toast-countdown"
          style={
            {
              backgroundColor: accent,
              // Drive the sweep duration off the toast's own auto-dismiss timer.
              animationDuration: `${item.duration}ms`,
            } as React.CSSProperties
          }
          aria-hidden="true"
        />
      )}
    </div>
  );
}

/**
 * Mount ONCE in App.tsx. Provides `useToast()` to the whole tree and renders the
 * stacked toast viewport in a fixed, top-of-stack overlay (bottom-center on
 * mobile, bottom-right on desktop). The viewport is pointer-events-none so it
 * never blocks clicks behind it; each toast re-enables pointer events for its
 * own dismiss/action controls.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((spec: ToastSpec) => {
    const id = nextId.current++;
    const duration =
      spec.duration === undefined
        ? DEFAULT_DURATION[spec.variant]
        : spec.duration;
    const item: ToastItem = { ...spec, id, duration };
    setToasts((prev) => {
      const next = [...prev, item];
      // Drop the oldest if we exceed the cap.
      return next.length > MAX_TOASTS
        ? next.slice(next.length - MAX_TOASTS)
        : next;
    });
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (message, options) =>
        show({ variant: "success", message, ...options }),
      error: (message, options) =>
        show({ variant: "error", message, ...options }),
      info: (message, options) =>
        show({ variant: "info", message, ...options }),
      // #2580: reversible-action toast -- info-styled, an Undo action + a
      // visible countdown line. Longer default window (6s) so the reversal is
      // real. Runs onUndo then dismisses; auto-dismiss simply skips onUndo.
      undo: (message, onUndo, options) =>
        show({
          variant: "info",
          message,
          duration: options?.duration ?? 6000,
          title: options?.title,
          countdown: true,
          action: { label: "Undo", onClick: onUndo },
        }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        // Fixed viewport. Newest toast renders LAST so it sits visually on top
        // of the stack (bottom of the column on screen).
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end"
        // The region wrapper is not itself a live region; each toast owns its
        // own role/aria-live so screen readers announce them individually.
        aria-hidden={toasts.length === 0 ? true : undefined}
      >
        {toasts.map((t) => (
          <ToastCard key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
